import React, { useState, useMemo, useEffect } from 'react';
import { Search, Users, AlertTriangle, Calendar, UserCheck, Clock, Timer, CheckCircle2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { LoadingSpinner } from '@/components/ui/loading-states';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useEmployees, type Employee } from '@/hooks/useEmployees';
import { useOrganizationsForSelect } from '@/hooks/organizations/useOrganizations';
import { 
  useAssignScheduleTemplate, 
  useTemplateAssignments,
  type TemplateAssignment,
} from '@/hooks/schedules/useScheduleTemplates';
import type { ScheduleTemplate } from '@/types/schedule.types';
import { APP_TIME_ZONE, todayAppDateString } from '@/utils/dateFormat';

interface AssignEmployeesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: ScheduleTemplate | null;
  onSuccess?: () => void;
}

export const AssignEmployeesDialog: React.FC<AssignEmployeesDialogProps> = ({
  open,
  onOpenChange,
  template,
  onSuccess,
}) => {
  // State for active tab
  const [activeTab, setActiveTab] = useState<string>('assign');

  // State for organization filter
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>('');

  // State for search
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // State for employee selection
  const [selectedEmployees, setSelectedEmployees] = useState<Set<string>>(new Set());

  // State for dates
  const [validFrom, setValidFrom] = useState<string>('');
  const [validUntil, setValidUntil] = useState<string>('');

  // State for temporary schedule
  const [isTemporary, setIsTemporary] = useState<boolean>(false);
  const [reason, setReason] = useState<string>('');

  // State for errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // State for success feedback
  const [successMessage, setSuccessMessage] = useState<string>('');

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedEmployees(new Set());
      setSelectedOrganizationId('');
      setValidFrom(todayAppDateString()); // Default to today
      setValidUntil('');
      setIsTemporary(false);
      setReason('');
      setErrors({});
      setSuccessMessage('');
      setSearch('');
      setDebouncedSearch('');
      setActiveTab('assign');
    }
  }, [open]);

  // Reset employee selection when organization changes
  useEffect(() => {
    setSelectedEmployees(new Set());
  }, [selectedOrganizationId]);

  // Hooks
  const { data: organizations = [] } = useOrganizationsForSelect();

  const { data: employeesData, isLoading: isLoadingEmployees } = useEmployees({
    search: debouncedSearch || undefined,
    limit: 500,
    isActive: true,
    organizationId: selectedOrganizationId || undefined,
  });

  const { 
    data: assignmentsData, 
    isLoading: isLoadingAssignments,
    refetch: refetchAssignments,
  } = useTemplateAssignments(open ? template?.id || null : null);

  const assignMutation = useAssignScheduleTemplate();

  // Employees list
  const employees = useMemo(() => {
    return employeesData?.data || [];
  }, [employeesData]);

  // Assigned employees list
  const assignedEmployees = useMemo(() => {
    return assignmentsData?.assignments || [];
  }, [assignmentsData]);

  // Toggle employee selection
  const toggleEmployee = (id: string) => {
    setSelectedEmployees(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Select all visible employees
  const selectAll = () => {
    setSelectedEmployees(new Set(employees.map(e => e.id)));
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedEmployees(new Set());
  };

  // Validate form
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (selectedEmployees.size === 0) {
      newErrors.employees = 'Выберите хотя бы одного сотрудника';
    }

    if (!validFrom) {
      newErrors.validFrom = 'Укажите дату начала действия';
    }

    // For temporary schedules, valid_until is required
    if (isTemporary && !validUntil) {
      newErrors.validUntil = 'Для временного графика обязательно укажите дату окончания';
    }

    if (validUntil && validFrom && new Date(validUntil) < new Date(validFrom)) {
      newErrors.validUntil = 'Дата окончания не может быть раньше даты начала';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit handler
  const handleSubmit = async () => {
    if (!template) return;
    if (!validate()) return;

    setSuccessMessage('');

    try {
      const assignedCount = selectedEmployees.size;
      const response = await assignMutation.mutateAsync({
        templateId: template.id,
        employeeIds: Array.from(selectedEmployees),
        validFrom,
        validUntil: validUntil || undefined,
        isTemporary: isTemporary || undefined,
        reason: reason || undefined,
      });

      // Clear selection after successful assignment
      setSelectedEmployees(new Set());

      // Refetch assignments to show updated list
      await refetchAssignments();

      // Show success message — prefer backend message, fall back to count
      const message = response?.message
        || `График назначен (${assignedCount} ${assignedCount === 1 ? 'сотрудник' : 'сотрудников'})`;
      setSuccessMessage(message);

      // Switch to "Назначенные" tab so user immediately sees the result
      setActiveTab('assigned');

      onSuccess?.();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Не удалось назначить график сотрудникам';
      setErrors(prev => ({
        ...prev,
        submit: errorMessage,
      }));
    }
  };

  // Close handler
  const handleClose = () => {
    if (!assignMutation.isPending) {
      onOpenChange(false);
    }
  };

  if (!template) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-12 h-12 bg-blue-100 dark:bg-blue-950/20 rounded-full flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <DialogTitle>Управление назначениями графика</DialogTitle>
              <DialogDescription>
                Шаблон: <strong>{template.name}</strong>
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <Tabs
          value={activeTab}
          onValueChange={(value) => {
            setActiveTab(value);
            if (value === 'assign') {
              setSuccessMessage('');
            }
          }}
          className="flex-1 flex flex-col min-h-0"
        >
          <TabsList className="w-full justify-start">
            <TabsTrigger value="assign" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Назначить
            </TabsTrigger>
            <TabsTrigger value="assigned" className="flex items-center gap-2">
              <UserCheck className="h-4 w-4" />
              Назначенные
              {assignedEmployees.length > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
                  {assignedEmployees.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Tab: Assign new employees */}
          <TabsContent value="assign" className="flex-1 flex flex-col min-h-0 mt-4 overflow-y-auto">
            {/* Temporary Schedule Checkbox */}
            <div className="flex items-center gap-3 pb-3 border-b">
              <Checkbox
                id="isTemporary"
                checked={isTemporary}
                onCheckedChange={(checked) => setIsTemporary(!!checked)}
              />
              <Label htmlFor="isTemporary" className="flex items-center gap-2 cursor-pointer">
                <Timer className="h-4 w-4 text-orange-500" />
                <span>Временный график</span>
              </Label>
              {isTemporary && (
                <span className="text-sm text-muted-foreground">
                  (после окончания автоматически восстановится предыдущий график)
                </span>
              )}
            </div>

            {/* Date Selection Section */}
            <div className="grid grid-cols-2 gap-4 pb-4 border-b">
              <div className="space-y-2">
                <Label htmlFor="validFrom" className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Дата начала действия <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="validFrom"
                  type="date"
                  value={validFrom}
                  onChange={(e) => {
                    setValidFrom(e.target.value);
                    if (errors.validFrom) {
                      setErrors(prev => ({ ...prev, validFrom: '' }));
                    }
                  }}
                  className={errors.validFrom ? 'border-destructive' : ''}
                />
                {errors.validFrom && (
                  <p className="text-sm text-destructive">{errors.validFrom}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="validUntil" className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Дата окончания {isTemporary ? (
                    <span className="text-destructive">*</span>
                  ) : (
                    <span className="text-muted-foreground text-xs">(необязательно)</span>
                  )}
                </Label>
                <Input
                  id="validUntil"
                  type="date"
                  value={validUntil}
                  onChange={(e) => {
                    setValidUntil(e.target.value);
                    if (errors.validUntil) {
                      setErrors(prev => ({ ...prev, validUntil: '' }));
                    }
                  }}
                  className={errors.validUntil ? 'border-destructive' : ''}
                />
                {errors.validUntil && (
                  <p className="text-sm text-destructive">{errors.validUntil}</p>
                )}
              </div>
            </div>

            {/* Reason field (for temporary schedules) */}
            {isTemporary && (
              <div className="space-y-2 pb-4 border-b">
                <Label htmlFor="reason" className="flex items-center gap-2">
                  Причина временного назначения
                  <span className="text-muted-foreground text-xs">(необязательно)</span>
                </Label>
                <Input
                  id="reason"
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Например: командировка, больничный, замена"
                  maxLength={500}
                />
              </div>
            )}

            {/* Organization filter and Search */}
            <div className="flex gap-4 py-2">
              {/* Organization filter */}
              <div className="w-64 flex-shrink-0">
                <Select
                  value={selectedOrganizationId || 'all'}
                  onValueChange={(value) => setSelectedOrganizationId(value === 'all' ? '' : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Все организации" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Все организации</SelectItem>
                    {organizations.map((org: { id: string; name: string }) => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Поиск сотрудников по имени..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Selection Actions */}
            <div className="flex items-center justify-between py-2">
              <div className="text-sm text-muted-foreground">
                Выбрано: <strong className="text-foreground">{selectedEmployees.size}</strong> из {employees.length}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={selectAll} disabled={employees.length === 0}>
                  Выбрать все
                </Button>
                <Button size="sm" variant="outline" onClick={clearSelection} disabled={selectedEmployees.size === 0}>
                  Снять выбор
                </Button>
              </div>
            </div>

            {/* Employees List */}
            <div className="flex-1 min-h-0 overflow-hidden">
              {isLoadingEmployees ? (
                <div className="h-full flex items-center justify-center py-12">
                  <LoadingSpinner className="h-8 w-8" />
                </div>
              ) : employees.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-gray-500 py-12">
                  <Users className="h-12 w-12 mb-3 opacity-50" />
                  <p className="font-medium">Сотрудники не найдены</p>
                  {search && (
                    <p className="text-sm mt-1">Попробуйте изменить параметры поиска</p>
                  )}
                </div>
              ) : (
                <div className="h-[300px] overflow-y-auto border rounded-lg">
                  {employees.map((employee) => (
                    <EmployeeRow
                      key={employee.id}
                      employee={employee}
                      isSelected={selectedEmployees.has(employee.id)}
                      onToggle={() => toggleEmployee(employee.id)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Errors */}
            {errors.employees && (
              <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-md mt-2">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span className="text-sm">{errors.employees}</span>
              </div>
            )}
            {errors.submit && (
              <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-md mt-2">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span className="text-sm">{errors.submit}</span>
              </div>
            )}
          </TabsContent>

          {/* Tab: View assigned employees */}
          <TabsContent value="assigned" className="flex-1 flex flex-col min-h-0 mt-4 overflow-y-auto">
            {successMessage && (
              <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-950/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-900 rounded-md mb-3">
                <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
                <span className="text-sm">{successMessage}</span>
              </div>
            )}
            {isLoadingAssignments ? (
              <div className="flex-1 flex items-center justify-center py-12">
                <LoadingSpinner className="h-8 w-8" />
              </div>
            ) : assignedEmployees.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-gray-500 py-12">
                <UserCheck className="h-12 w-12 mb-3 opacity-50" />
                <p className="font-medium">Нет назначенных сотрудников</p>
                <p className="text-sm mt-1">Перейдите на вкладку "Назначить" для добавления</p>
              </div>
            ) : (
              <div className="h-[400px] overflow-y-auto border rounded-lg">
                {assignedEmployees.map((assignment) => (
                  <AssignedEmployeeRow
                    key={assignment.id}
                    assignment={assignment}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={assignMutation.isPending}
          >
            {activeTab === 'assigned' ? 'Закрыть' : 'Отмена'}
          </Button>
          {activeTab === 'assign' && (
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={assignMutation.isPending || selectedEmployees.size === 0}
              className="min-w-[140px]"
            >
              {assignMutation.isPending ? (
                <>
                  <LoadingSpinner className="mr-2 h-4 w-4" />
                  Назначение...
                </>
              ) : (
                <>
                  <Users className="mr-2 h-4 w-4" />
                  Назначить ({selectedEmployees.size})
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Subcomponent for employee row (for assignment)
interface EmployeeRowProps {
  employee: Employee;
  isSelected: boolean;
  onToggle: () => void;
}

const EmployeeRow: React.FC<EmployeeRowProps> = ({ employee, isSelected, onToggle }) => {
  return (
    <div
      className={`flex items-center gap-3 p-3 border-b last:border-b-0 cursor-pointer transition-colors ${
        isSelected
          ? 'bg-blue-50 dark:bg-blue-950/20'
          : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
      }`}
      onClick={onToggle}
    >
      <Checkbox
        checked={isSelected}
        onCheckedChange={onToggle}
        onClick={(e) => e.stopPropagation()}
      />
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">
          {employee.last_name} {employee.first_name} {employee.middle_name || ''}
        </div>
        <div className="text-sm text-muted-foreground truncate">
          {employee.position?.name || 'Без должности'}
          {employee.department?.name && ` • ${employee.department.name}`}
        </div>
      </div>
      <Badge variant={employee.is_active ? 'default' : 'secondary'}>
        {employee.is_active ? 'Активен' : 'Неактивен'}
      </Badge>
    </div>
  );
};

// Subcomponent for assigned employee row (for viewing)
interface AssignedEmployeeRowProps {
  assignment: TemplateAssignment;
}

const AssignedEmployeeRow: React.FC<AssignedEmployeeRowProps> = ({ assignment }) => {
  const employee = assignment.employee;

  // Format date for display
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Бессрочно';
    return new Date(dateStr).toLocaleDateString('ru-RU', {
      timeZone: APP_TIME_ZONE,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Get assignment type badge
  const getAssignmentTypeBadge = () => {
    const type = (assignment as { assignment_type?: string }).assignment_type;
    switch (type) {
      case 'temporary':
        return (
          <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
            <Timer className="h-3 w-3 mr-1" />
            Временный
          </Badge>
        );
      case 'continuation':
        return (
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
            Продолжение
          </Badge>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex items-center gap-3 p-3 border-b last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-800/50">
      <div className="flex-shrink-0 w-10 h-10 bg-green-100 dark:bg-green-950/20 rounded-full flex items-center justify-center">
        <UserCheck className="w-5 h-5 text-green-600 dark:text-green-400" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">
          {employee ? (
            `${employee.last_name} ${employee.first_name} ${employee.middle_name || ''}`
          ) : (
            <span className="text-muted-foreground italic">Сотрудник не найден</span>
          )}
        </div>
        <div className="text-sm text-muted-foreground truncate">
          {employee?.position?.name || 'Без должности'}
          {employee?.department?.name && ` • ${employee.department.name}`}
          {(assignment as { reason?: string }).reason && (
            <span className="italic"> • {(assignment as { reason?: string }).reason}</span>
          )}
        </div>
      </div>
      {getAssignmentTypeBadge()}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Clock className="h-4 w-4" />
        <span>
          {formatDate(assignment.valid_from)} — {formatDate(assignment.valid_until)}
        </span>
      </div>
      {employee && (
        <Badge variant={employee.is_active ? 'default' : 'secondary'}>
          {employee.is_active ? 'Активен' : 'Неактивен'}
        </Badge>
      )}
    </div>
  );
};
